import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format, addDays } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { EstimateLineItems } from './EstimateLineItems';
import { useCreateEstimate, useSendEstimate, useEstimateValidDays } from '@/hooks/useEstimates';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { netFromGross, taxFromNet } from '@/utils/tax';
import { createEstimateSchema, type CreateEstimateInput, type EstimateLineItem } from '@/types/estimate';
import { Loader2, Send } from 'lucide-react';

interface EstimateFromLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: {
    id: string;
    customer_name: string;
    customer_phone: string | null;
    customer_email: string | null;
    customer_address: string | null;
    issue_description: string;
    quoted_price: number | null;
    customer_id: string | null;
  };
  /** Fired after an estimate has been emailed to the customer (not on draft save). */
  onEstimateSent?: () => void;
}

export function EstimateFromLeadDialog({
  open,
  onOpenChange,
  lead,
  onEstimateSent,
}: EstimateFromLeadDialogProps) {
  const createEstimate = useCreateEstimate();
  const sendEstimate = useSendEstimate();
  const tax = useCompanyTax();
  const validDays = useEstimateValidDays();
  const [lineItems, setLineItems] = useState<EstimateLineItem[]>([]);
  const [isSending, setIsSending] = useState(false);

  const form = useForm<CreateEstimateInput>({
    resolver: zodResolver(createEstimateSchema),
    defaultValues: {
      lead_id: lead.id,
      customer_id: lead.customer_id || undefined,
      customer_name: lead.customer_name,
      customer_phone: lead.customer_phone || '',
      sent_to_email: lead.customer_email || '',
      customer_address: lead.customer_address || '',
      service_description: lead.issue_description,
      line_items: [],
      valid_until: format(addDays(new Date(), validDays), 'yyyy-MM-dd'),
      notes: '',
      terms: `Estimate valid for ${validDays} days from date of issue.`,
    },
  });

  // Initialize line items from lead's quoted price
  useEffect(() => {
    if (lead.quoted_price && lead.quoted_price > 0) {
      // Quoted price is gross (tax-inclusive); strip tax for the line item.
      const lineItemPrice = netFromGross(lead.quoted_price, tax);
      setLineItems([{
        description: lead.issue_description || 'Service as discussed',
        quantity: 1,
        unit_price: lineItemPrice,
        amount: lineItemPrice,
      }]);
    } else {
      setLineItems([{
        description: lead.issue_description || 'Service',
        quantity: 1,
        unit_price: 0,
        amount: 0,
      }]);
    }
  }, [lead, tax]);

  // Sync line items with form
  useEffect(() => {
    form.setValue('line_items', lineItems);
  }, [lineItems, form]);

  const handleSaveDraft = async () => {
    const values = form.getValues();
    values.line_items = lineItems;
    
    const isValid = await form.trigger();
    if (!isValid) return;

    createEstimate.mutate(values, {
      onSuccess: () => {
        onOpenChange(false);
        form.reset();
      },
    });
  };

  const handleSendNow = async () => {
    const values = form.getValues();
    values.line_items = lineItems;
    
    const isValid = await form.trigger();
    if (!isValid) return;
    if (!values.sent_to_email?.trim()) {
      form.setError('sent_to_email', { message: 'Add an email address to send this estimate online' });
      return;
    }

    setIsSending(true);
    
    createEstimate.mutate(values, {
      onSuccess: (data) => {
        // Now send the estimate
        sendEstimate.mutate(
          { estimateId: data.id },
          {
            onSuccess: () => {
              setIsSending(false);
              onOpenChange(false);
              form.reset();
              onEstimateSent?.();
            },
            onError: () => {
              setIsSending(false);
            },
          }
        );
      },
      onError: () => {
        setIsSending(false);
      },
    });
  };

  const subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
  const gstAmount = taxFromNet(subtotal, tax);
  const total = subtotal + gstAmount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Estimate</DialogTitle>
          <DialogDescription>
            Create an estimate for {lead.customer_name}. You can save as draft or send immediately.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="customer_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Customer Name *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="sent_to_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} value={field.value || ''} placeholder="Needed to send online; optional for a printed quote" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="customer_phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="valid_until"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valid Until</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="customer_address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service Address</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="service_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      {...field} 
                      value={field.value || ''} 
                      rows={3}
                      placeholder="Describe the work to be performed"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div>
              <FormLabel>Line Items *</FormLabel>
              <div className="mt-2">
                <EstimateLineItems
                  items={lineItems}
                  onChange={setLineItems}
                />
              </div>
              {form.formState.errors.line_items && (
                <p className="text-sm text-destructive mt-1">
                  {form.formState.errors.line_items.message}
                </p>
              )}
            </div>

            <FormField
              control={form.control}
              name="terms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Terms & Conditions</FormLabel>
                  <FormControl>
                    <Textarea 
                      {...field} 
                      value={field.value || ''} 
                      rows={2}
                      placeholder={`Estimate valid for ${validDays} days from date of issue.`}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Internal Notes</FormLabel>
                  <FormControl>
                    <Textarea 
                      {...field} 
                      value={field.value || ''} 
                      rows={2}
                      placeholder="Notes for internal use only (not shown to customer)"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handleSaveDraft}
            disabled={createEstimate.isPending || isSending}
          >
            {createEstimate.isPending && !isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Draft
          </Button>
          <Button
            type="button"
            onClick={handleSendNow}
            disabled={createEstimate.isPending || isSending || !form.watch('sent_to_email')}
          >
            {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Send className="mr-2 h-4 w-4" />
            Send Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
